import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { EducationalDisclaimer } from "@/components/EducationalDisclaimer";
import { withNetworkUtm } from "@/lib/network";

export default function Disclaimer() {
  const page = webPageSchema({
    path: "/disclaimer",
    title: "Medical & Educational Disclaimer | Shariq Refai, MD, MBA",
    description:
      "The medical and educational-use disclaimer for shariqrefai.com. This website is general education and does not provide medical advice, diagnosis, or treatment.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Disclaimer", path: "/disclaimer" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="Medical & Educational Disclaimer | Shariq Refai, MD, MBA"
        description="The medical and educational-use disclaimer for shariqrefai.com. This website is general education and does not provide medical advice, diagnosis, or treatment."
        url="/disclaimer"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Disclaimer", path: "/disclaimer" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Disclaimer</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Medical &amp; Educational Disclaimer
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            Education, not medical care.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: May 16, 2026.</strong>
          </p>

          <EducationalDisclaimer variant="article" className="not-prose mt-0 mb-8" />

          <p>
            This website does not provide diagnosis, treatment, therapy,
            psychiatric evaluation, crisis intervention, or individualized
            medical recommendations.
          </p>
          <p>
            This website is not monitored for clinical or crisis
            communications.
          </p>
          <p>
            Books, articles, frameworks, exercises, and educational materials
            referenced on this website are for general educational use only
            and should not be relied upon as a substitute for professional
            care.
          </p>

          <p>
            The information on shariqrefai.com (the "website"), including all
            articles, books, videos, podcasts, and other content authored by or
            attributed to Shariq Refai, MD, MBA, is provided for general
            educational and informational purposes only.
          </p>

          <h2>No Medical Advice, Therapy, or Psychiatric Treatment</h2>
          <p>
            Nothing on this website constitutes medical advice, psychiatric
            treatment, therapy, diagnosis, individualized recommendations,
            or a physician-patient or therapist-client relationship. The
            content is reflective and educational in nature and is intended
            to support general mental health literacy, not to provide care.
          </p>

          <h2>No Pharmacological Guidance</h2>
          <p>
            Nothing on this website is medication, dosing, prescribing,
            deprescribing, or pharmacological guidance of any kind. Any
            mention of a class of medication, a specific medication, an
            indication, a side effect, or a dosing pattern is for general
            educational illustration only. Never start, stop, change, or
            adjust the dose of any medication based on content from this
            website. Speak with your prescribing clinician before making any
            change.
          </p>

          <h2>No Physician-Patient Relationship</h2>
          <p>
            Reading this website, reading a book or article authored by
            Shariq Refai, downloading materials, sending a message through
            the contact form, emailing, or following Shariq Refai on any
            platform does not create a physician-patient, therapist-client,
            treatment, fiduciary, or confidential medical relationship
            between you and Shariq Refai or any affiliated organization,
            including shrinkMD, shrinQ, or Unstuck.
          </p>

          <h2>No Practice of Medicine</h2>
          <p>
            This website does not offer or constitute the practice of
            medicine in any state, territory, or country. Dr. Refai's
            clinical practice is conducted exclusively through shrinkMD
            under separately maintained state licenses. The fact that Dr.
            Refai holds medical licensure in one or more jurisdictions does
            not create any clinical relationship through this website or
            extend any license to readers in any other jurisdiction.
          </p>

          <h2>Frameworks Are Not Medical Devices</h2>
          <p>
            Any educational frameworks referenced on this website, including
            any "reset," "practice," or step-by-step exercise (such as the
            four-step reset described in <em>The Havoc in Your Head</em>),
            are general educational tools for self-reflection and emotional
            literacy. They are not medical devices, diagnostic tools,
            therapeutic protocols, or treatments for any condition,
            including anxiety disorders, panic disorder, generalized
            anxiety, OCD, PTSD, or any other diagnosis. They are not
            intended to diagnose, treat, cure, mitigate, or prevent any
            disease or condition.
          </p>

          <h2>Composite Patient Narratives</h2>
          <p>
            Any patient narratives, case-style anecdotes, or "I had a
            patient" framings that appear in books, essays, articles, or
            commentary by Dr. Refai are educational composites. They combine
            and alter details from multiple real and hypothetical
            situations. They do not depict any individual identifiable
            person, and no narrative should be read as a description of any
            actual patient of Dr. Refai or shrinkMD. These narratives are
            presented for educational illustration only.
          </p>

          <h2>No Emergency, Crisis, or Monitoring Services</h2>
          <p>
            This website is not an emergency or crisis service. It is not
            monitored for urgent or time-sensitive medical or mental health
            concerns. Messages submitted through the website may not be seen
            for some time and should never be relied on for any matter that
            requires prompt attention.
          </p>

          <h2>Books, Articles, and Frameworks</h2>
          <p>
            Any books, articles, essays, frameworks, exercises, reflections,
            and conversations referenced or hosted on this website are
            educational and informational only. They are not personalized
            mental health care, are not a substitute for evaluation by a
            qualified clinician, and are not intended to diagnose or treat
            any condition.
          </p>

          <h2>Always Consult a Licensed Clinician</h2>
          <p>
            Always seek the advice of your physician, psychiatrist, or another
            qualified licensed health provider with any questions you may have
            regarding a medical or mental health condition. Never disregard
            professional medical advice or delay in seeking it because of
            something you have read on this website.
          </p>

          <h2>No Outcomes Promised; Individual Results Vary</h2>
          <p>
            The website does not promise or imply any specific outcome. No
            information, framework, or practice on this website is offered
            as a treatment or remedy for any medical or mental health
            condition. Individual results vary, and what is useful for one
            reader may not be useful for another.
          </p>

          <h2>When to Seek Professional Care</h2>
          <p>
            If your symptoms are severe, persistent, worsening, or impairing
            your ability to function, please seek evaluation by a qualified
            licensed clinician. The educational content on this website is
            not a substitute for that evaluation.
          </p>

          <h2>Crisis Resources</h2>
          <p>
            If you are in crisis or feel unsafe, call or text{" "}
            <a href="tel:988">988</a> in the United States, call{" "}
            <a href="tel:911">911</a>, or go to the nearest emergency room.
            Outside the United States, contact your local emergency services.
          </p>
          <p>
            Seek immediate help if you experience any of the following:
          </p>
          <ul>
            <li>Thoughts of suicide, self-harm, or wanting to die.</li>
            <li>Inability to keep yourself safe right now.</li>
            <li>A plan or means to harm yourself or someone else.</li>
            <li>Hallucinations, severe confusion, or a sudden change in your ability to function safely.</li>
            <li>Symptoms that feel medically urgent, including chest pain, fainting, severe physical restriction, or signs of overdose.</li>
          </ul>

          <h2>Language and Tone of Educational Content</h2>
          <p>
            Educational content on this website is written to help readers
            reflect on, explore, understand, and learn about mental health
            topics. It is not written to monitor, detect, predict, diagnose,
            or treat any condition. Words like "reset," "practice," "tool,"
            "exercise," and "framework" refer to educational reflection
            aids, not medical interventions, devices, or therapeutic
            protocols.
          </p>

          <h2>Quizzes, Symptom Tools, and Reflection Features</h2>
          <p>
            If quizzes, symptom-reflection prompts, calculators, or
            self-assessment features are added to this website now or in
            the future, they are informational reflection tools, not
            diagnostic instruments. They will never say "you have" or "you
            likely have" any condition. Where neutral language is used (for
            example, "your responses may reflect patterns sometimes
            associated with..."), it is a framing aid, not a medical
            finding. Results are informational only and are not a
            diagnosis, an assessment of severity, or a treatment
            recommendation. Always seek evaluation by a qualified licensed
            clinician for any actual concern. See the{" "}
            <Link href="/community-guidelines">Community Guidelines</Link>{" "}
            for the broader framework that applies to any reader-facing
            tool.
          </p>

          <h2>International Scope</h2>
          <p>
            Content on shariqrefai.com is written primarily for a United
            States audience. Healthcare systems, clinical guidelines, drug
            availability, regulatory bodies, and standards of care differ
            significantly across jurisdictions. References to medications,
            treatment options, levels of care, insurance, emergency
            services, or specific resources may not apply outside the
            United States. Readers outside the US should rely on local
            clinical guidance and licensed clinicians familiar with their
            healthcare system. Nothing on this site should be read as
            extending Dr. Refai's clinical practice into any jurisdiction
            in which he is not licensed.
          </p>

          <h2>Third-Party Resources</h2>
          <p>
            This website may reference or link to third-party services,
            including{" "}
            <a href={withNetworkUtm("https://shrinkmd.com/", "disclaimer_page")} target="_blank" rel="noopener noreferrer">
              shrinkMD
            </a>
            ,{" "}
            <a href={withNetworkUtm("https://shrinq.com/", "disclaimer_page")} target="_blank" rel="noopener noreferrer">
              shrinQ
            </a>
            , and{" "}
            <a href={withNetworkUtm("https://beunstuck.app/", "disclaimer_page")} target="_blank" rel="noopener noreferrer">
              Unstuck
            </a>
            . Each of those services has its own terms, privacy policies, and
            clinical scope, and clinical care, when offered, is provided
            through those entities and their licensed clinicians, not through
            this website.
          </p>

          <h2>Updates</h2>
          <p>
            This disclaimer may be updated from time to time. Continued use of
            the website after changes are posted constitutes acceptance of the
            updated disclaimer.
          </p>

          <p>
            Questions about this disclaimer can be sent through the{" "}
            <Link href="/contact">contact page</Link> or email{" "}
            <a href="mailto:hello@shariqrefai.com">hello@shariqrefai.com</a>.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
